import { DateDisplay } from './DateDisplay';
import { useTimezoneStore } from '@/stores/timezoneStore';
import { formatInUserTimezone } from '@/utils/timezone';

interface TzDateDisplayProps {
  /**
   * The UTC date value to display
   */
  utcDate: Date | string | number;
  /**
   * Format string for date-fns (default: 'MMM d, yyyy h:mm a')
   * Use 'MMM d, yyyy' for date-only displays
   */
  formatString?: string;
  /**
   * Whether this is a datetime (show timezone badge) or date-only (no badge)
   * Default: true (show timezone badge)
   */
  showUtcBadge?: boolean;
  /**
   * Whether to show the timezone tooltip
   * Default: true
   */
  showTimezoneTooltip?: boolean;
  /**
   * Optional className for the container
   */
  className?: string;
  /**
   * Whether this is date-only (no time component)
   * Default: false
   */
  dateOnly?: boolean;
}

/**
 * Timezone-aware DateDisplay wrapper
 *
 * Automatically converts UTC dates to the user's selected timezone for display.
 * Updates automatically when the user changes their timezone preference.
 *
 * Usage:
 * ```tsx
 * <TzDateDisplay utcDate="2025-12-01T15:00:00Z" />
 * // Shows: Dec 1, 2025 10:00 AM (in America/New_York)
 *
 * <TzDateDisplay utcDate="2025-12-01T15:00:00Z" dateOnly formatString="MMM d, yyyy" />
 * // Shows: Dec 1, 2025
 * ```
 */
export function TzDateDisplay({
  utcDate,
  formatString = 'MMM d, yyyy h:mm a',
  showUtcBadge = true,
  showTimezoneTooltip = true,
  dateOnly = false,
  className,
}: TzDateDisplayProps) {
  const { timezone } = useTimezoneStore();

  // Convert UTC date to user's timezone for display
  const formattedDate = formatInUserTimezone(utcDate, timezone, formatString);

  return (
    <DateDisplay
      utcDate={utcDate}
      showUtcBadge={showUtcBadge}
      showTimezoneTooltip={showTimezoneTooltip}
      dateOnly={dateOnly}
      className={className}
    >
      {formattedDate}
    </DateDisplay>
  );
}
